// ─────────────────────────────────────────────────────────────
// App.js — Root component with React Router
//
// Replaces the old `view` state-switch with proper routes. Children that
// still call `setView(id)` keep working: we pass a setView adapter that
// navigates to the matching route, so feature components don't need
// rewrites yet.
//
// Routes:
//   /                  → Dashboard
//   /entry             → TradeEntry
//   /review            → TradeReview
//   /checklist         → TradeChecklist
//   /analytics         → Analytics
//   /market            → Market
//   /journal           → TradeLog
//   /profile           → ProfileSettings
//   /strategies        → ManageStrategies
//   /portfolio         → AccountManager
//   /discipline        → DisciplineAnalytics
//   /legal/:doc        → LegalDocument
//   /reset-password    → ResetPasswordPage
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";

import {
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  authFetch,
} from "./lib/supabase";
import { C } from "./lib/colors";

import { useAuth } from "./hooks/useAuth";
import { useSupabase } from "./hooks/useSupabase";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { useStrategyPreferences } from "./hooks/useStrategyPreferences";

import { Dashboard } from "./features/dashboard/Dashboard";
import { HorizontalDashboard } from "./features/dashboard/HorizontalDashboard";
import { TradeEntry } from "./features/trades/TradeEntry";
import { TradeReview } from "./features/trades/TradeReview";
import { TradeChecklist } from "./features/trades/TradeChecklist";
import { TradeLog } from "./features/trades/TradeLog";
import { ManageStrategies } from "./features/trades/ManageStrategies";
import { Analytics } from "./features/analytics/Analytics";
import { DisciplineAnalytics } from "./features/analytics/DisciplineAnalytics";
import { EvalSurvival } from "./features/eval-survival/EvalSurvival";
import { Market } from "./features/market/Market";
import { ProfileSettings } from "./features/account/ProfileSettings";
import { RestoreAccountGate } from "./features/account/RestoreAccountGate";
import { ReConsentGate, isLegalConsentOutOfDate } from "./features/account/ReConsentGate";
import { RulesSettings } from "./features/settings/RulesSettings";
import { AccountManager } from "./features/account/AccountManager";
import { LegalDocument } from "./features/legal/LegalDocument";
import { GuideIndex } from "./features/guide/GuideIndex";
import { GuideWelcome } from "./features/guide/pages/Welcome";
import { GuideDashboard } from "./features/guide/pages/Dashboard";
import { GuideTradeEntry } from "./features/guide/pages/TradeEntry";
import { GuideTradeReview } from "./features/guide/pages/TradeReview";
import { GuideJournal } from "./features/guide/pages/Journal";
import { GuideAnalytics } from "./features/guide/pages/Analytics";
import { GuideEvalSurvival } from "./features/guide/pages/EvalSurvival";
import { GuidePortfolio } from "./features/guide/pages/Portfolio";
import { GuideCanITrade } from "./features/guide/pages/CanITrade";
import { GuideStrategies } from "./features/guide/pages/Strategies";
import { GuideMarket } from "./features/guide/pages/Market";
import { GuideProfile } from "./features/guide/pages/Profile";
import { GuideBilling } from "./features/guide/pages/Billing";

import { LandingPage } from "./features/landing/LandingPage";
import { TradeCardGenerator } from "./features/landing/TradeCardGenerator";
import { ResetPasswordScreen } from "./features/auth/ResetPasswordScreen";
import { BillingPage } from "./features/billing/BillingPage";
import { SubscriptionGate } from "./features/billing/SubscriptionGate";
import { OnboardingWizard } from "./features/account/OnboardingWizard";

import { TradeHistoryModal } from "./components/TradeHistoryModal";
import { ToastContainer } from "./components/ui/ToastContainer";
import "./styles/MODAL_STYLES.css";

// Vercel Web Analytics — auto-tracks pageviews + visitor metrics.
// Aliased to VercelAnalytics because we already have our own Analytics
// component imported from features/analytics/Analytics. Mounted at the
// app root so it tracks every page including unauthenticated views
// (landing page, legal docs).
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";

// ─────────────────────────────────────────────────────────────
// NAV — bottom bar + sidebar
// ─────────────────────────────────────────────────────────────
const NAV = [
  { id: "dashboard",  path: "/",           label: "Dashboard",   emoji: "🏠" },
  { id: "entry",      path: "/entry",      label: "Trade Entry", emoji: "✍️" },
  { id: "review",     path: "/review",     label: "Review",      emoji: "🧠" },
  { id: "analytics",  path: "/analytics",  label: "Analytics",   emoji: "📊" },
  { id: "eval",       path: "/eval",       label: "Eval Survival", emoji: "🛡️" },
  { id: "market",     path: "/market",     label: "Market",      emoji: "🌍" },
  { id: "journal",    path: "/journal",    label: "Journal",     emoji: "📋" },
  { id: "strategies", path: "/strategies", label: "Strategies",  emoji: "🎯" },
  { id: "portfolio",  path: "/portfolio",  label: "Portfolio",   emoji: "💰" },
];

// Map old view ids → new paths so children that still call setView("entry")
// keep working without being rewritten.
const VIEW_TO_PATH = {
  dashboard:  "/",
  entry:      "/entry",
  review:     "/review",
  checklist:  "/checklist",
  analytics:  "/analytics",
  market:     "/market",
  journal:    "/journal",
  profile:    "/profile",
  billing:    "/billing",
  strategies: "/strategies",
  portfolio:  "/portfolio",
  discipline: "/discipline",
  "getting-started": "/guide/welcome",
  "guide":           "/guide",
};

const PATH_TO_VIEW = Object.fromEntries(
  Object.entries(VIEW_TO_PATH).map(([k, v]) => [v, k])
);

// ─────────────────────────────────────────────────────────────
// Wrapper for ResetPasswordScreen → ResetPasswordPage flow.
// (Old code used a state machine; React Router handles it cleanly.)
// ─────────────────────────────────────────────────────────────
function RecoveryRouteHandler({ isLoggedIn, supabase }) {
  const params = new URLSearchParams(window.location.search);
  const tokenHash = params.get("token_hash");
  const type = params.get("type");

  if (tokenHash && type === "recovery" && !isLoggedIn) {
    return (
      <ResetPasswordScreen
        tokenHash={tokenHash}
        supabaseClient={supabase}
        onComplete={() => (window.location.href = "/")}
        onCancel={() => (window.location.href = "/")}
      />
    );
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// TrialEndingBanner — shown when trial ends in ≤3 days and
// no payment method is on file yet.
// ─────────────────────────────────────────────────────────────
function TrialEndingBanner({ profile, onAddCard }) {
  if (!profile?.trial_ending_soon) return null;
  if (profile?.is_gifted) return null;  // gifted accounts never see trial nudges

  const trialEndDate = profile?.trial_end_date
    ? new Date(profile.trial_end_date).toLocaleDateString("en-US", {
        month: "short", day: "numeric",
      })
    : null;

  return (
    <div
      style={{
        background: "linear-gradient(90deg, #7c3aed22, #f59e0b22)",
        borderBottom: "1px solid #f59e0b55",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        fontSize: 13,
        color: "#f0f2ff",
        flexWrap: "wrap",
      }}
    >
      <span>
        ⏳{" "}
        <strong>Your free trial ends{trialEndDate ? ` on ${trialEndDate}` : " soon"}</strong>
        {" "}— add a card to keep access to all your data and detectors.
      </span>
      <button
        onClick={onAddCard}
        style={{
          background: "#f59e0b",
          color: "#000",
          border: "none",
          borderRadius: 6,
          padding: "6px 14px",
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        Add Card →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Authenticated shell — sidebar, top bar, bottom nav, routes
// ─────────────────────────────────────────────────────────────
function AuthenticatedShell({
  session,
  profile,
  updateProfile,
  refreshProfile,
  signOut,
  getDailyNote,
  saveDailyNote,
  isConfigured,
  userId,
  supabase,
  showOnboarding,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // Bridge old setView(viewId) calls → router navigation
  const setView = (viewId) => {
    const path = VIEW_TO_PATH[viewId];
    if (path) navigate(path);
  };

  // For highlighting the active nav button
  const currentView = PATH_TO_VIEW[location.pathname] || "dashboard";
  const activeNav = ["checklist"].includes(currentView) ? "entry" : currentView;

  // Demo account protection — block trade modifications for demo users
  const DEMO_USER_ID = "4baa5323-2ab9-4c7a-8128-ff89bf0deff1";
  const isDemoAccount = userId === DEMO_USER_ID;

  // Subscription helpers
  const openBilling = () => navigate("/billing");

  // Billing needs the user's attention when:
  //   - Cancellation is queued (cancel_at_period_end = true while still active/trialing)
  //   - Payment failed (past_due)
  //   - Trial is ending soon (no card on file)
  // All states render a small yellow dot on the Billing nav item.
  // Default to horizontal on desktop when no preference is saved.
  // Always respect an explicit user choice on any device.
  const isHorizontal =
    profile?.dashboard_layout === "horizontal" ||
    (profile?.dashboard_layout == null && window.innerWidth >= 1024);

  const billingNeedsAttention =
    profile?.cancel_at_period_end === true ||
    profile?.subscription_status === "past_due" ||
    (profile?.trial_ending_soon === true && !profile?.is_gifted);

  // Poll subscription status every 5 minutes so cancellations take effect
  // without requiring the user to log out and back in.
  const profileStatusRef = useRef(profile?.subscription_status);
  useEffect(() => {
    profileStatusRef.current = profile?.subscription_status;
  }, [profile?.subscription_status]);

  useEffect(() => {
    if (!userId) return;
    const checkSubscription = async () => {
      try {
        const res = await authFetch(
          `${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=subscription_status,subscription_end,is_gifted`
        );
        if (!res.ok) return;
        const [data] = await res.json();
        if (!data) return;
        // If subscription status changed, reload to pick up new state
        if (data.subscription_status !== profileStatusRef.current) {
          window.location.reload();
        }
      } catch (e) {
        // Silent fail — polling is best-effort
      }
    };
    const interval = setInterval(checkSubscription, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(interval);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Stripe redirect back to app.
  // Previously this used a 4-second timeout + reload, which was fragile —
  // if the webhook took longer than 4 seconds, the reload would read the
  // still-stale localStorage profile and dump the user on the upgrade gate.
  // Now we poll refreshProfile() until subscription_status flips, then
  // navigate to /. Caps at ~15 seconds to avoid an infinite poll if the
  // webhook never fires (e.g. misconfigured endpoint).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("subscription") !== "success") return;

    setProcessingSubscription(true);

    let cancelled = false;
    let attempts  = 0;
    const maxAttempts = 15; // 15 attempts × 1s = 15s max wait

    const poll = async () => {
      if (cancelled || attempts >= maxAttempts) {
        setProcessingSubscription(false);
        navigate("/", { replace: true });
        return;
      }
      attempts++;
      await refreshProfile?.();
      // The next render will see the updated profile; if it's now
      // trialing/active, we stop polling. We check after a short delay
      // because setProfile is async via React state.
      setTimeout(() => {
        if (cancelled) return;
        // Re-read the latest profile from localStorage as a fallback
        // signal — refreshProfile writes there directly.
        try {
          const cached = JSON.parse(localStorage.getItem("fos_profile") || "null");
          if (cached?.subscription_status === "trialing" || cached?.subscription_status === "active") {
            setProcessingSubscription(false);
            navigate("/", { replace: true });
            return;
          }
        } catch {}
        poll();
      }, 1000);
    };

    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Handle return from Stripe Billing Portal.
  //
  // When the user clicks Manage Billing, makes a change in Stripe's portal
  // (cancel, un-cancel, update payment method), and clicks "Return to
  // FuturesOS", they land back here with ?billing=updated on the URL.
  //
  // Without a refresh, the cached profile in localStorage still reflects the
  // PRE-portal state — so UI signals like the "Cancels on …" callout, the
  // sidebar dot, etc. show stale data until the 5-minute background poll
  // catches up. We poll briefly to give the Stripe webhook time to fire,
  // then strip the query param so a future page load doesn't re-trigger.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("billing") !== "updated") return;

    let cancelled = false;
    let attempts  = 0;
    const maxAttempts = 8; // 8 × 750ms = 6s max wait; webhook is usually <2s

    const poll = async () => {
      if (cancelled || attempts >= maxAttempts) {
        // Strip the param either way — we've done what we can.
        navigate(location.pathname, { replace: true });
        return;
      }
      attempts++;
      await refreshProfile?.();
      setTimeout(() => {
        if (cancelled) return;
        poll();
      }, 750);
    };

    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Data layer
  const {
    trades,
    tradesLoading,
    addTrade,
    deleteTrade,
    updateTrade,
    customStrategies,
    addCustomStrategy,
    deleteCustomStrategy,
  } = useSupabase(userId);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSetup, setShowSetup] = useLocalStorage("fos_setup_banner", true);
  const [processingSubscription, setProcessingSubscription] = useState(false);

  // ─── Currently-selected account ──────────────────────────────────────
  // Persists across page reloads via localStorage (fos_current_account_id).
  // Initialized to null; the useEffect below auto-selects the first account
  // when the accounts list loads so the UI never lies about its state.
  const [currentAccountId, setCurrentAccountId] = useLocalStorage(
    "fos_current_account_id",
    null
  );

  const { strategyPreferences, setStrategyPreferences, loading: strategyPrefsLoading } =
    useStrategyPreferences(currentAccountId, supabase, userId);

  // Trade-review modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedTradeForReview, setSelectedTradeForReview] = useState(null);
  const openTradeReview = (trade) => {
    setSelectedTradeForReview(trade);
    setShowReviewModal(true);
  };
  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedTradeForReview(null);
  };

  // Accounts
  const [accounts, setAccounts] = useState([]);
  const fetchAccounts = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/accounts?user_id=eq.${userId}&is_active=eq.true&is_blown=eq.false`
      );
      const data = await res.json();
      setAccounts(data || []);
    } catch (e) {
      console.error("Failed to fetch accounts", e);
    }
  }, [userId]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // Auto-select the first account when accounts load if nothing valid is
  // selected. Closes the gap where currentAccountId could be null but the
  // dropdown visually showed the first account (browser fallback), causing
  // menu writes to land as user-wide (account_id=null) rows.
  //
  // Cases this handles:
  //   - First login: stored id is null → pick accounts[0]
  //   - Restored from localStorage but account was deleted: stored id no
  //     longer exists in accounts list → pick accounts[0]
  //   - Already-valid stored id: stillValid=true, no-op (no flicker)
  useEffect(() => {
    if (!Array.isArray(accounts) || accounts.length === 0) return;
    const stillValid = accounts.some(a => a.id === currentAccountId);
    if (!stillValid) {
      setCurrentAccountId(accounts[0].id);
    }
  }, [accounts, currentAccountId, setCurrentAccountId]);

  // Note: news-poller used to be triggered from the browser every 15 minutes.
  // That fires hundreds of unnecessary calls (every user × every 15 min) and
  // also fails on the publishable Supabase key. The Edge Function should run
  // on a Supabase cron schedule instead. NewsWidget reads from news_cache
  // directly.

  const displayName =
    profile?.display_name || session?.user?.email?.split("@")[0] || "Trader";
  const avatarLetter = displayName[0].toUpperCase();

  // Common props bundle for feature pages
  const commonProps = {
    trades,
    tradesLoading,
    accounts,
    currentAccountId,
    setCurrentAccountId,
    setView,
    userId,
    profile,
    isDemoAccount,
  };

  // Show processing screen while waiting for Stripe webhook
  if (processingSubscription) {
    return (
      <div style={{
        minHeight: "100vh", background: "#080a14",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        color: "#e2e8f0", fontFamily: "'DM Sans', sans-serif",
        textAlign: "center", gap: 16,
      }}>
        <div style={{ fontSize: 48, animation: "spin 1s linear infinite" }}>⚙️</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Activating your subscription…</div>
        <div style={{ fontSize: 14, color: "#8892a4" }}>This takes just a moment. Please wait.</div>
      </div>
    );
  }

  return (
    <>
      {showOnboarding && (
        <OnboardingWizard
          userId={userId}
          displayName={displayName}
          setView={setView}
          fetchAccounts={fetchAccounts}
          onComplete={async (destination) => {
            await refreshProfile?.();
            if (destination === "billing") {
              // Go straight to Stripe — skip the BillingPage middleman
              try {
                const res = await authFetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
                  method: "POST",
                  body: JSON.stringify({
                    priceId:    "price_1TYvITL8HSUTWmPdvaawKuRA",
                    successUrl: `${window.location.origin}/?subscription=success`,
                    cancelUrl:  `${window.location.origin}/`,
                  }),
                });
                const data = await res.json();
                if (data.url) { window.location.href = data.url; return; }
              } catch (e) {
                console.error("[Onboarding] Stripe checkout failed:", e);
              }
              // Fallback to dashboard if Stripe fails
              navigate("/");
            } else if (destination) {
              setView(destination);
            }
          }}
          onSkip={async () => { await refreshProfile?.(); setView("dashboard"); }}
        />
      )}
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        fontFamily: "'DM Sans', -apple-system, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { background: #08090f; color: #f0f2ff; min-height: 100%; }
        body { overflow-x: hidden; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1c1f30; border-radius: 4px; }
        input:focus, select:focus, textarea:focus { border-color: #4f8ef7 !important; box-shadow: 0 0 0 2px rgba(79,142,247,0.15) !important; }
        @keyframes pulse { 0%,100% { opacity: 0.3; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1); } }
        button { font-family: inherit; }

        /* ─────────────────────────────────────────────
           MOBILE RESPONSIVENESS — applies below 768px
           Class-based so individual components can opt in.
           ───────────────────────────────────────────── */
        @media (max-width: 768px) {
          /* Main content gets less padding on mobile */
          .fos-main { padding: 12px 12px 100px !important; }

          /* Two-column grids (e.g. dashboard: chart + sidebar) → single column */
          .fos-grid-2col { grid-template-columns: 1fr !important; }

          /* Multi-column stat grids → 2 columns instead of 4 */
          .fos-grid-stats-4 { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }

          /* 3-column action grids (Log/Review/CanITrade) → single column */
          .fos-grid-actions-3 { grid-template-columns: 1fr !important; }

          /* Tables: allow horizontal scroll instead of breaking layout */
          .fos-scroll-x { overflow-x: auto; -webkit-overflow-scrolling: touch; }

          /* Modals: take more of the viewport on mobile */
          .fos-modal { width: 95vw !important; max-width: 95vw !important; max-height: 92vh !important; }

          /* Form rows: stack labels above inputs (already does on most) */
          .fos-form-row { flex-direction: column !important; gap: 8px !important; }

          /* Buttons in rows: full width and stacked */
          .fos-btn-row { flex-direction: column !important; }
          .fos-btn-row > * { width: 100% !important; }

          /* Section titles smaller on mobile */
          h2 { font-size: 18px !important; }

          /* Top nav — hide subtitle and reset columns on mobile to prevent overflow */
          .fos-nav-subtitle { display: none !important; }
          .fos-nav-reset { display: none !important; }
          .fos-nav-greeting { font-size: 12px !important; }
          .fos-nav-clock { font-size: 10px !important; }
          h3 { font-size: 15px !important; }
        }

        /* Tap-friendly inputs everywhere */
        input, select, textarea, button {
          font-size: 14px;
        }
        @media (max-width: 768px) {
          input, select, textarea {
            font-size: 16px !important; /* prevents iOS zoom on focus */
          }
          button {
            min-height: 40px;
          }
        }
      `}</style>

      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 12px",
          borderBottom: `1px solid ${C.border}`,
          position: "sticky",
          top: 0,
          background: C.bg + "ee",
          backdropFilter: "blur(12px)",
          zIndex: 50,
          overflow: "hidden",
          minHeight: 52,
          gap: 8,
        }}
      >
        <button
          onClick={() => setSidebarOpen((o) => !o)}
          style={{
            background: "none",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            width: 36,
            height: 36,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{ width: 15, height: 1.5, background: C.sub, borderRadius: 1 }}
            />
          ))}
        </button>

        <div
          style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 14 }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              background: `linear-gradient(135deg, ${C.green}, ${C.blue})`,
              borderRadius: 7,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            📊
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: "0.4px",
                color: C.text,
              }}
            >
              FUTURES.OS
            </div>
            <div style={{ fontSize: 9, color: C.blue, letterSpacing: "0.12em" }}>
              {isConfigured ? "LIVE" : "DEMO"}
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* Welcome message — centered in top bar, desktop horizontal layout only */}
        {isHorizontal && (
          <div style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}>
            <div className="fos-nav-greeting" style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {displayName} 👋
            </div>
            <div className="fos-nav-subtitle" style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", marginTop: 1 }}>
              Discipline today. Freedom tomorrow.
            </div>
          </div>
        )}

        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Discord community link */}
          <a
            href="https://discord.gg/Xt8D9yuWd"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "transparent",
              border: `1px solid ${C.border}`,
              borderRadius: 5,
              color: C.muted,
              fontSize: 10,
              padding: "3px 8px",
              cursor: "pointer",
              fontWeight: 500,
              letterSpacing: "0.04em",
              fontFamily: "monospace",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "#5865F2"; e.currentTarget.style.borderColor = "#5865F2"; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderColor = C.border; }}
          >
            💬 Discord
          </a>

          {/* Reset columns button — only in horizontal layout, hidden on mobile */}
          {isHorizontal && (
            <button
              className="fos-nav-reset"
              onClick={() => {
                localStorage.removeItem("fos_dash_cols");
                window.dispatchEvent(new Event("fos_reset_cols"));
              }}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 5,
                color: C.muted,
                fontSize: 10,
                padding: "3px 8px",
                cursor: "pointer",
                fontWeight: 500,
                letterSpacing: "0.04em",
                fontFamily: "monospace",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = C.text; e.currentTarget.style.borderColor = C.text; }}
              onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderColor = C.border; }}
            >
              ↺ Reset columns
            </button>
          )}
          <div className="fos-nav-clock" style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>
            {new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            ET
          </div>
        </div>
      </div>

      {/* Trial ending banner — only shows when trial_ending_soon is true */}
      <TrialEndingBanner
        profile={profile}
        onAddCard={openBilling}
      />

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 60,
            backdropFilter: "blur(4px)",
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: 220,
          background: "#0b0d19",
          borderRight: `1px solid ${C.border}`,
          zIndex: 70,
          display: "flex",
          flexDirection: "column",
          padding: "20px 0",
          transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        <div
          style={{
            padding: "0 16px 20px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              background: `linear-gradient(135deg, ${C.green}, ${C.blue})`,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            📊
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.text }}>
              FUTURES.OS
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: C.muted,
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            ✕
          </button>
        </div>
        <nav style={{ flex: 1, padding: "16px 10px", overflowY: "auto" }}>
          {[...NAV,
            { id: "guide", path: "/guide", label: "Guide", emoji: "📖" },
            { id: "profile", path: "/profile", label: "Profile", emoji: "👤" },
            { id: "billing", path: "/billing", label: "Billing", emoji: "💳" },
          ].map(
            (item) => {
              const active = activeNav === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "none",
                    background: active ? C.green + "10" : "transparent",
                    color: active ? C.green : C.muted,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: active ? 700 : 400,
                    marginBottom: 2,
                    transition: "all 0.15s",
                    borderLeft: `2px solid ${active ? C.green : "transparent"}`,
                  }}
                >
                  <span style={{ fontSize: 16, position: "relative", display: "inline-flex" }}>
                    {item.emoji}
                    {item.id === "billing" && billingNeedsAttention && (
                      <span
                        aria-label="Subscription needs attention"
                        style={{
                          position: "absolute",
                          top: -2,
                          right: -4,
                          width: 8,
                          height: 8,
                          background: C.yellow,
                          borderRadius: "50%",
                          border: `1.5px solid ${C.bg || "#0d1124"}`,
                        }}
                      />
                    )}
                  </span>
                  {item.label}
                </button>
              );
            }
          )}

          {/* Discord */}
          <a
            href="https://discord.gg/Xt8D9yuWd"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              color: C.muted,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 400,
              marginBottom: 2,
              transition: "all 0.15s",
              borderLeft: "2px solid transparent",
              textDecoration: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "#5865F2"; e.currentTarget.style.borderColor = "#5865F2"; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderColor = "transparent"; }}
          >
            <span style={{ fontSize: 16 }}>💬</span>
            Discord Community
          </a>

          {/* Contact */}
          <a
            href="mailto:admin@futures-os.app"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              color: C.muted,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 400,
              marginBottom: 2,
              transition: "all 0.15s",
              borderLeft: "2px solid transparent",
              textDecoration: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = C.text; e.currentTarget.style.borderColor = C.text; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderColor = "transparent"; }}
          >
            <span style={{ fontSize: 16 }}>✉️</span>
            Contact
          </a>

          {/* Sign out */}
          <button
            onClick={() => { signOut(); setSidebarOpen(false); }}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              color: C.red,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 400,
              marginTop: 4,
              transition: "all 0.15s",
              borderLeft: "2px solid transparent",
            }}
          >
            <span style={{ fontSize: 16 }}>🚪</span>
            Sign Out
          </button>
        </nav>
        <div
          onClick={() => {
            navigate("/profile");
            setSidebarOpen(false);
          }}
          style={{
            padding: "14px 16px",
            borderTop: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#ffffff05")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.blue}, ${C.purple})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {avatarLetter}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: C.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </div>
            <div style={{ fontSize: 10, color: C.blue }}>Edit Profile →</div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className="fos-main"
        style={{
          flex: 1,
          padding: isHorizontal ? "10px 10px 90px" : "20px 16px 90px",
          maxWidth: isHorizontal ? "100%" : 960,
          margin: isHorizontal ? "0" : "0 auto",
          width: "100%",
        }}
      >
        <Routes>
          {/* Subscription required for every route — Trade Entry included.
              Without a Portfolio account (which is itself subscription-gated),
              Trade Entry has no functional purpose for a non-subscribed user,
              so showing the upgrade card is more useful than a dead-end form. */}
          <Route
            path="/entry"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Trade Entry">
                <TradeEntry
                  {...commonProps}
                  addTrade={addTrade}
                  updateTrade={updateTrade}
                  customStrategies={customStrategies}
                  strategyPreferences={strategyPreferences}
                  userId={userId}
                  supabase={supabase}
                />
              </SubscriptionGate>
            }
          />

          {/* Subscription required for everything else */}
          <Route
            path="/"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Dashboard">
                {isHorizontal ? (
                  <HorizontalDashboard
                    {...commonProps}
                    showSetup={showSetup && !isConfigured}
                    setShowSetup={setShowSetup}
                    displayName={displayName}
                    profile={profile}
                    getDailyNote={getDailyNote}
                    saveDailyNote={saveDailyNote}
                    openTradeReview={openTradeReview}
                    updateTrade={updateTrade}
                  />
                ) : (
                  <Dashboard
                    {...commonProps}
                    showSetup={showSetup && !isConfigured}
                    setShowSetup={setShowSetup}
                    displayName={displayName}
                    profile={profile}
                    getDailyNote={getDailyNote}
                    saveDailyNote={saveDailyNote}
                    openTradeReview={openTradeReview}
                    updateTrade={updateTrade}
                  />
                )}
              </SubscriptionGate>
            }
          />
          <Route path="/review" element={
            <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Trade Review">
              <TradeReview {...commonProps} />
            </SubscriptionGate>
          } />
          <Route
            path="/checklist"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Trade Checklist">
                <TradeChecklist setView={setView} userId={userId} currentAccountId={currentAccountId} accounts={accounts} trades={trades} tradesLoading={tradesLoading} profile={profile} />
              </SubscriptionGate>
            }
          />
          <Route
            path="/analytics"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Analytics">
                <Analytics
                  {...commonProps}
                  getDailyNote={getDailyNote}
                  saveDailyNote={saveDailyNote}
                />
              </SubscriptionGate>
            }
          />
          <Route
            path="/eval"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Eval Survival">
                <EvalSurvival {...commonProps} />
              </SubscriptionGate>
            }
          />
          <Route path="/market" element={
            <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Market Intelligence">
              <Market accounts={accounts} />
            </SubscriptionGate>
          } />
          <Route
            path="/journal"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Trade Journal">
                <TradeLog
                  {...commonProps}
                  deleteTrade={deleteTrade}
                  updateTrade={updateTrade}
                  openTradeReview={openTradeReview}
                />
              </SubscriptionGate>
            }
          />
          <Route
            path="/billing"
            element={
              <BillingPage profile={profile} />
            }
          />
          <Route
            path="/profile"
            element={
              <ProfileSettings
                profile={profile}
                updateProfile={updateProfile}
                signOut={signOut}
                setView={setView}
                supabase={supabase}
                userId={userId}
                accounts={accounts}
                currentAccountId={currentAccountId}
                trades={trades}
              />
            }
          />
          <Route
            path="/settings/rules"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Rules Settings">
              <RulesSettings
                userId={userId}
                accounts={accounts}
                currentAccountId={currentAccountId}
                setCurrentAccountId={setCurrentAccountId}
              />
              </SubscriptionGate>
            }
          />
          <Route
            path="/strategies"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Strategies">
              <ManageStrategies
                customStrategies={customStrategies}
                addCustomStrategy={addCustomStrategy}
                deleteCustomStrategy={deleteCustomStrategy}
                strategyPreferences={strategyPreferences}
                setStrategyPreferences={setStrategyPreferences}
                strategyPrefsLoading={strategyPrefsLoading}
                profile={profile}
                updateProfile={updateProfile}
                setView={setView}
              />
              </SubscriptionGate>
            }
          />
          <Route
            path="/portfolio"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Portfolio">
              <AccountManager
                userId={userId}
                setView={setView}
                accounts={accounts}
                setAccounts={setAccounts}
              />
              </SubscriptionGate>
            }
          />
          <Route
            path="/portfolio/roi"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Portfolio">
              <AccountManager
                userId={userId}
                setView={setView}
                accounts={accounts}
                setAccounts={setAccounts}
                defaultTab="roi"
              />
              </SubscriptionGate>
            }
          />
          <Route
            path="/discipline"
            element={
              <SubscriptionGate profile={profile} onUpgrade={openBilling} feature="Discipline Analytics">
                <DisciplineAnalytics {...commonProps} />
              </SubscriptionGate>
            }
          />
          <Route path="/legal/:doc" element={<LegalDocumentRoute />} />
          {/* Guide — full product reference. Accessible to everyone,
              including non-subscribers, so they can read what the product
              does before/during their trial. Pages are built incrementally;
              :slug fallback shows a "coming soon" placeholder for slugs
              that are listed in the GuideIndex but not yet implemented. */}
          <Route path="/guide"              element={<GuideIndex />} />
          <Route path="/guide/welcome"      element={<GuideWelcome />} />
          <Route path="/guide/dashboard"    element={<GuideDashboard />} />
          <Route path="/guide/trade-entry"  element={<GuideTradeEntry />} />
          <Route path="/guide/trade-review" element={<GuideTradeReview />} />
          <Route path="/guide/journal"      element={<GuideJournal />} />
          <Route path="/guide/analytics"     element={<GuideAnalytics />} />
          <Route path="/guide/eval-survival" element={<GuideEvalSurvival />} />
          <Route path="/guide/portfolio"     element={<GuidePortfolio />} />
          <Route path="/guide/can-i-trade"   element={<GuideCanITrade />} />
          <Route path="/guide/strategies"    element={<GuideStrategies />} />
          <Route path="/guide/market"        element={<GuideMarket />} />
          <Route path="/guide/profile"       element={<GuideProfile />} />
          <Route path="/guide/billing"       element={<GuideBilling />} />
          <Route path="/guide/:slug"         element={<GuidePlaceholder />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Bottom Nav */}
      <nav
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: C.bg + "f0",
          borderTop: `1px solid ${C.border}`,
          backdropFilter: "blur(16px)",
          display: "flex",
          zIndex: 50,
          paddingBottom: "env(safe-area-inset-bottom, 4px)",
        }}
      >
        {NAV.map((item) => {
          const active = activeNav === item.id;
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              style={{
                flex: 1,
                padding: "10px 4px 8px",
                background: "none",
                border: "none",
                color: active ? C.green : C.muted,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                transition: "color 0.15s",
                position: "relative",
              }}
            >
              {active && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 24,
                    height: 2,
                    background: C.green,
                    borderRadius: "0 0 3px 3px",
                  }}
                />
              )}
              <span style={{ fontSize: 18, position: "relative", display: "inline-flex" }}>
                {item.emoji}
                {item.id === "billing" && billingNeedsAttention && (
                  <span
                    aria-label="Subscription needs attention"
                    style={{
                      position: "absolute",
                      top: -2,
                      right: -4,
                      width: 9,
                      height: 9,
                      background: C.yellow,
                      borderRadius: "50%",
                      border: `1.5px solid ${C.bg || "#0d1124"}`,
                    }}
                  />
                )}
              </span>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: active ? 700 : 400,
                  letterSpacing: "0.03em",
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* TRADE HISTORY MODAL */}
      {showReviewModal && selectedTradeForReview && (
        <TradeHistoryModal
          isOpen={showReviewModal}
          trade={selectedTradeForReview}
          onClose={closeReviewModal}
          updateTrade={updateTrade}
          customStrategies={customStrategies}
          accounts={accounts}
        />
      )}
    </div>
    </>
  );
}

function LegalDocumentRoute() {
  const { doc } = useParams();
  // The original LegalDocument accepts a `title` prop; map URL segment → title.
  const titles = {
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    disclaimer: "Disclaimer",
  };
  return <LegalDocument title={titles[doc] || "Legal"} />;
}

// Placeholder for Guide pages that are registered in the index but not yet
// written. Lets the index link to every section without producing dead links —
// the page itself just says "coming soon" with a link back to the Guide.
function GuidePlaceholder() {
  const { slug } = useParams();
  const pretty = slug
    ? slug.split("-").map(s => s[0].toUpperCase() + s.slice(1)).join(" ")
    : "This page";
  return (
    <div style={{ maxWidth: 600, margin: "40px auto", padding: 20, textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📝</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
        {pretty} — coming soon
      </h2>
      <p style={{ fontSize: 13, color: "#999", marginBottom: 20, lineHeight: 1.6 }}>
        This page is part of the Guide but hasn't been written yet. New
        pages are being added regularly.
      </p>
      <a href="/guide" style={{ fontSize: 13, color: "#4a9eff", textDecoration: "underline" }}>
        ← Back to Guide
      </a>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Disposable email blocking
// Checked client-side before any Supabase signup call fires.
// Add domains freely — the Set lookup is O(1).
// ─────────────────────────────────────────────────────────────
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  // Guerrilla Mail family
  "guerrillamail.com","guerrillamail.net","guerrillamail.org","guerrillamail.biz",
  "guerrillamail.de","guerrillamail.info","guerrillamailblock.com",
  "sharklasers.com","grr.la","spam4.me",
  // Mailinator family
  "mailinator.com","mailinator.net","mailinator.org","suremail.info",
  "spikio.com","chammy.info","bobmail.info","binkmail.info",
  // 10 Minute Mail / Temp Mail variants
  "10minutemail.com","10minutemail.net","10minutemail.org","10minutemail.de",
  "tempmail.com","tempmail.net","tempmail.org","temp-mail.org","temp-mail.io",
  "tempinbox.com","tempemail.com","tempemail.net",
  // Yopmail
  "yopmail.com","yopmail.fr","cool.fr.nf","jetable.fr.nf","nospam.ze.tc",
  "nomail.xl.cx","mega.zik.dj","speed.1s.fr","courriel.fr.nf","ypmail.weebly.com",
  // Trashmail family
  "trashmail.com","trashmail.net","trashmail.org","trashmail.at",
  "trashmail.io","trashmail.me","trashmail.xyz",
  // Throwaway / discard
  "throwam.com","throwam.info","discard.email","discardmail.com","discardmail.de",
  "mailnull.com","maildrop.cc","mailscrap.com","mailexpire.com","mailimate.com",
  // Spam trap / misc
  "spamgourmet.com","spamgourmet.net","spamgourmet.org",
  "spambox.us","spamfree24.org","spamspot.com","spamgap.com",
  "spamoff.de","spaml.com","spaml.de","spamthisplease.com",
  "spamhereplease.com","spamherelots.com",
  // Getairmail / filzmail / misc generators
  "getairmail.com","filzmail.com","mt2015.com","owlpic.com",
  "crazymailing.com","getnada.com","fake-box.com","fakeinbox.com",
  "emailondeck.com","pookmail.com","trbvm.com","klzlk.com",
  // Wegwerfmail (German disposables)
  "wegwerfmail.de","wegwerfmail.net","wegwerfmail.org",
  // Dispostable
  "dispostable.com",
]);

const isDisposableEmail = (email = "") => {
  const domain = email.trim().split("@")[1]?.toLowerCase();
  return domain ? DISPOSABLE_EMAIL_DOMAINS.has(domain) : false;
};

// ─────────────────────────────────────────────────────────────
// App — auth gate
// ─────────────────────────────────────────────────────────────
function AppInner() {
  const {
    session,
    profile,
    authError,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile,
    getDailyNote,
    saveDailyNote,
    isLoggedIn,
  } = useAuth();
  const userId = session?.user?.id || null;
  const location = useLocation();

  // Wrap the raw signUp from useAuth with a disposable-email check.
  // Returns { error } shape so LandingPage can surface it via authError.
  const signUpWithEmailCheck = useCallback(
    async (email, password, name, consent = null) => {
      if (isDisposableEmail(email)) {
        return {
          error: {
            message:
              "Please use a permanent email address. Disposable and temporary email services are not supported.",
          },
        };
      }
      return signUp(email, password, name, consent);
    },
    [signUp]
  );

  // Allow legal document pages through any gating logic — users must be
  // able to read the Terms / Privacy / Disclaimer they're being asked to
  // accept. Without this, the ReConsentGate creates an infinite loop:
  // user clicks "Read Terms" → /legal/terms loads in a new tab → that tab
  // mounts AppInner → gate renders on top because consent is still stale.
  const isOnLegalPage = location.pathname.startsWith("/legal/");

  // isConfigured is derived purely from env vars — no need to mount useSupabase
  // again here. Calling useSupabase a second time creates a *separate* trades
  // state that races with the one in AuthenticatedShell, leading to silent
  // duplicate writes to both localStorage and the database.
  const isConfigured = !!(SUPABASE_URL && SUPABASE_ANON_KEY);

  // Lightweight supabase wrapper used by ResetPasswordScreen / ProfileSettings
  // (preserves shape of the inline wrapper from the old App)
  const supabase = {
    from: (table) => ({
      select: (columns = "*") => ({
        eq: (col, val) => ({
          single: async () => {
            try {
              const res = await authFetch(
                `${SUPABASE_URL}/rest/v1/${table}?${col}=eq.${val}&select=${columns}&user_id=eq.${userId}`
              );
              const data = await res.json();
              return { data: data?.[0] || null, error: null };
            } catch (e) {
              return { data: null, error: e };
            }
          },
        }),
      }),
    }),
  };

  // Recovery flow takes precedence over login screen
  const recoveryNode = (
    <RecoveryRouteHandler isLoggedIn={isLoggedIn} supabase={supabase} />
  );
  const params = new URLSearchParams(window.location.search);
  if (params.get("token_hash") && params.get("type") === "recovery" && !isLoggedIn) {
    return recoveryNode;
  }

  if (!isLoggedIn) {
    if (location.pathname === "/trade-card") {
      return <TradeCardGenerator />;
    }
    return (
      <LandingPage
        onSignIn={signIn}
        onSignUp={signUpWithEmailCheck}
        authError={authError}
      />
    );
  }

  // Account-deletion gate: if the user soft-deleted their account, show
  // the restore prompt instead of the normal app. Replaces the whole shell
  // rather than guarding inside it — avoids hook-order issues when restore
  // succeeds and we transition back to the normal render path.
  if (profile?.deleted_at) {
    return (
      <RestoreAccountGate
        profile={profile}
        onRestored={refreshProfile}
        onSignOut={signOut}
      />
    );
  }

  // Re-consent gate: when Terms or Privacy versions are bumped, existing
  // users' stored agreement is stale. Block the app until they accept the
  // new versions (or sign out). Same shape as RestoreAccountGate — full
  // replacement of the shell, not a wrapper, to keep hook order clean.
  // Skip the gate on /legal/* routes so users can read the documents
  // they're being asked to accept (otherwise it's an infinite loop).
  if (isLegalConsentOutOfDate(profile) && !isOnLegalPage) {
    return (
      <ReConsentGate
        profile={profile}
        userId={userId}
        onAccepted={refreshProfile}
        onSignOut={signOut}
      />
    );
  }

  return (
    <AuthenticatedShell
      session={session}
      profile={profile}
      updateProfile={updateProfile}
      refreshProfile={refreshProfile}
      signOut={signOut}
      getDailyNote={getDailyNote}
      saveDailyNote={saveDailyNote}
      isConfigured={isConfigured}
      userId={userId}
      supabase={supabase}
      showOnboarding={!!profile && profile.onboarding_completed === false}
    />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
      <ToastContainer />
      <VercelAnalytics />
    </BrowserRouter>
  );
}
